import {Card, DataStatus, Separator} from 'platform/components';
import {Box, Grid, Heading, Show, Text, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import type {FeatureCategory} from '@dms/api/new-vehicle';
import {getNewVehicleEnumLabel} from '@dms/features/new-vehicle';
import i18n from '@dms/i18n';

const t = (key: string, defaultValue: string) => i18n.t(key, {defaultValue});

export type StandardEquipmentCardProps = {
  state: 'noAccess' | 'unavailable' | 'error' | 'ready';
  groups: {category: FeatureCategory; features: {id: string; name: string}[]}[];
  isLoading: boolean;
  onRetry: VoidFunction;
};

export function StandardEquipmentCard(props: StandardEquipmentCardProps) {
  const count = props.groups.reduce((sum, group) => sum + group.features.length, 0);
  const emptyMessage = match(props.state)
    .with('noAccess', () =>
      t(
        'entity.newVehicle.vehicleDetail.equipmentNoAccess',
        'Standard equipment needs access to the new vehicle catalogue'
      )
    )
    .with('unavailable', () =>
      t(
        'entity.newVehicle.vehicleDetail.equipmentUnavailable',
        'This trim level is no longer published in the catalogue, so its equipment cannot be shown'
      )
    )
    .otherwise(() =>
      t(
        'entity.newVehicle.vehicleDetail.noStandardEquipment',
        'No standard equipment is listed for this trim level'
      )
    );

  return (
    <Card
      title={t('entity.newVehicle.vehicleDetail.standardEquipment', 'Standard equipment')}
      flags={
        count > 0 ? [{label: count.toString(), colorScheme: 'neutral', isSubtle: true}] : undefined
      }
      data-testid="new-vehicle-standard-equipment"
    >
      <DataStatus
        isLoading={props.state === 'ready' && props.isLoading}
        isError={props.state === 'error'}
        errorAction={{title: i18n.t('general.actions.retry'), onClick: props.onRetry}}
        isEmpty={count === 0}
        emptyMessage={emptyMessage}
      >
        {/* Same layout as the vehicle overview's All features card; read-only, so labels instead of chips. */}
        {props.groups.map((group, index) => (
          <Box key={group.category}>
            <Show when={index > 0}>
              <Separator />
            </Show>
            <VStack spacing={4}>
              <Heading size={4} headingLevel={5}>
                {getNewVehicleEnumLabel('featureCategory', group.category, t)}
              </Heading>
              <Grid columns={[1, 1, 2, 3]} spacing={2}>
                {group.features.map((feature) => (
                  <Box key={feature.id} paddingHorizontal={2} paddingVertical={1}>
                    <Text size="xSmall">{feature.name}</Text>
                  </Box>
                ))}
              </Grid>
            </VStack>
          </Box>
        ))}
      </DataStatus>
    </Card>
  );
}
